import * as React from 'react'

import { useStickyState } from '~shared/components/Sticky/useStickyState'
import { menuScreenElementIds } from '~shared/config/menuScreenElementIds'
import { IconChevronUp } from '~ui/components/icons/specific/IconChevrons'

import { useProductListSelectionCtx } from '../../segments/ProductList'
import { FloatingButton } from './FloatingButton'

const { stickyTopContentId: stickyTopBoxId } = menuScreenElementIds

type TFloatingButtonProps = Pick<
  PropsOf<typeof FloatingButton>,
  'bottom' | 'right' | 'marginRight' | 'screenMaxContentWidth'
>

type TProps = NoChildren & TFloatingButtonProps

/**
 * Fixed button rendered on the bottom-right of the screen
 * that will move the window scroll to the top on click.
 * ! Works only with window scroll overflow.
 *
 * * It is used only on the menu screen currently, to use it on other screens,
 * * abstract to multiple components, as the `useProductListSelectionCtx`
 * * from the `ProductListSelectionProvider` is used here and it is menu screen specific.
 */
export const ScrollToTopButton: React.FC<TProps> = (floatingButtonProps) => {
  const { scrollToCategoryWithoutObserver } = useProductListSelectionCtx()

  const isSearchBarStuck = useStickyState(stickyTopBoxId)

  const scrollToTop = React.useCallback(() => {
    // turn off the intersection observer while scrolling to top
    scrollToCategoryWithoutObserver()

    window.scrollTo({
      top: 0,
      behavior: `smooth`,
    })
  }, [scrollToCategoryWithoutObserver])

  return (
    <FloatingButton
      {...floatingButtonProps}
      isActive={isSearchBarStuck}
      icon={IconChevronUp}
      iconSize={`1.5rem`}
      bgColor={`tertiary`}
      iconColor={`specialColors.company`}
      padding={`small`}
      onClick={scrollToTop}
    />
  )
}
